import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { AddUserSchema } from "../../FormsValidations/Validations";
import { createUser, fetchUsersData, fetchUserRoles } from "../../Redux/Thunks/usersThunk";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { getTeamsListFromApiData } from "../Teams/teamsDataUtils";
import Input from "../../Utility/Input/Input";
import Label from "../../Utility/Label/Label";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import ErrorMsg from "../../Utility/ErrorMsg";
import { logger } from "../../utils/logger";
import { RiEditLine, RiInformationLine } from "react-icons/ri";
import { getListFromApiResponse, mapListItemToOption, getErrorMessage } from "./userMappers";
import "./users.css";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import ExpandableSection from "../../Utility/ExpandableSection/ExpandableSection";
import { digitsOnly } from "../../utils/digitsOnlyInput";

export default function AddUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const teamsData = useSelector(selectTeamsData);
  const prevPath = location.state?.previousPath || "/users";

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isContactExpanded, setIsContactExpanded] = useState(true);
  const [isUsernameEditable, setIsUsernameEditable] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [jobTitleList, setJobTitleList] = useState([]);

  const teamList = useMemo(() => getTeamsListFromApiData(teamsData) ?? [], [teamsData]);
  const teamOptions = useMemo(
    () => teamList.map((t) => ({ value: t.id, label: t.teamName ?? t.name ?? String(t.id) })),
    [teamList]
  );
  const roleOptions = useMemo(
    () => roleList.map((r) => ({ value: r.id, label: r.name })),
    [roleList]
  );
  const jobTitleOptions = useMemo(() => jobTitleList.map(mapListItemToOption), [jobTitleList]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddUserSchema),
    defaultValues: {
      role: "",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      jobTitle: "",
      teamId: "",
      status: "Active",
      billingRatePerHour: "",
      sendInviteEmail: true,
      forcePasswordResetOnFirstLogin: true,
      receiveWeeklyActivitySummary: false,
      mobileNumber: "",
      whatsappNumber: "",
    },
  });

  const watchedEmail = watch("email");

  useEffect(() => {
    if (!teamsData) dispatch(fetchTeamsData({ getAll: true }));
  }, [dispatch, teamsData]);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchUserRoles({ force: true }))
      .unwrap()
      .then((raw) => {
        if (!cancelled) setRoleList(getListFromApiResponse(raw));
      })
      .catch(() => {
        if (!cancelled) setRoleList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("JOB_TITLE"))
      .unwrap()
      .then((raw) => setJobTitleList(getListFromApiResponse(raw)))
      .catch(() => setJobTitleList([]));
  }, [dispatch]);

  useEffect(() => {
    if (watchedEmail && !isUsernameEditable) {
      const emailParts = watchedEmail.split("@");
      if (emailParts[0]) {
        const generatedUsername = emailParts[0].toLowerCase().replace(/[^a-z0-9]/g, ".");
        setValue("username", generatedUsername, { shouldValidate: true });
      }
    }
  }, [watchedEmail, isUsernameEditable, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    setSubmitError("");
    try {
      const payload = {
        email: data.email.trim(),
        roleId: data.role?.trim() || undefined,
        firstName: data.firstName?.trim() ?? "",
        lastName: data.lastName?.trim() ?? "",
        teamId: data.teamId?.trim() || undefined,
        jobTitle: data.jobTitle?.trim() ?? "",
        billingRatePerHour: Number(data.billingRatePerHour) || 0,
        is_active: data.status === "Active",
        sendInviteEmail: Boolean(data.sendInviteEmail),
        forcePasswordResetOnFirstLogin: Boolean(data.forcePasswordResetOnFirstLogin),
        mobileNumber: data.mobileNumber?.trim() || undefined,
        whatsappNumber: data.whatsappNumber?.trim() || undefined,
        receiveWeeklyActivitySummary: Boolean(data.receiveWeeklyActivitySummary),
      };
      await dispatch(createUser(payload)).unwrap();
      dispatch(fetchUsersData());
      navigate(prevPath);
    } catch (error) {
      logger.error("Error adding user:", error);
      setSubmitError(getErrorMessage(error) || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="w-full flex-1">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-1 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <div>
                    <Typography variant="h4" className="mb-0 text-theme-primary">
                      Add user
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            <Typography variant="text2" className="text-text-secondary mb-3">
              Invite a new user with role, team, job title and billing rate. Invite email and
              password reset options can be set below.
            </Typography>

            <div
              className="relative flex flex-col min-w-0 rounded-md border border-border-primary shadow-sm mb-5 overflow-y-auto bg-surface-primary"
              style={{ height: "calc(100vh - 300px)" }}
            >
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)} className="pb-4">
                  {submitError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                      {submitError}
                    </div>
                  )}

                  {/* Section 1: Identity & Role */}
                  <div className="mb-6">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Identity & Role
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Controller
                          name="role"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Role"
                              options={roleOptions}
                              placeholder="Select Role"
                              error={errors.role?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="firstName" required>
                          First Name
                        </Label>
                        <Input
                          type="text"
                          {...register("firstName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${errors.firstName ? "border-red-500" : ""}`}
                          id="firstName"
                          placeholder="Enter first name"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.firstName?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="lastName" required>
                          Last Name
                        </Label>
                        <Input
                          type="text"
                          {...register("lastName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${errors.lastName ? "border-red-500" : ""}`}
                          id="lastName"
                          placeholder="Enter last name"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.lastName?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="email" required>
                          Email
                        </Label>
                        <Input
                          type="email"
                          {...register("email")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${errors.email ? "border-red-500" : ""}`}
                          id="email"
                          placeholder="Enter email"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.email?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="username">Username</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            {...register("username")}
                            disabled={!isUsernameEditable}
                            className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary pr-10 ${errors.username ? "border-red-500" : ""} ${!isUsernameEditable ? "bg-gray-50" : ""}`}
                            id="username"
                            placeholder="Auto-generated from email"
                            noWrapper={true}
                          />
                          {!isUsernameEditable && (
                            <Button
                              type="button"
                              variant="icon"
                              size="vsm"
                              title="Edit username"
                              onClick={() => setIsUsernameEditable(true)}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-theme-primary"
                            >
                              <RiEditLine size={18} />
                            </Button>
                          )}
                        </div>
                        <Typography variant="text2" className="text-text-secondary text-xs mt-1">
                          Username auto-generated from email{" "}
                          {isUsernameEditable && " • Editable via ✎ icon"}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Access & Organization */}
                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Access & Organization
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Controller
                          name="teamId"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Team"
                              options={teamOptions}
                              placeholder="Select Team"
                              error={errors.teamId?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Controller
                          name="jobTitle"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Job Title"
                              options={jobTitleOptions}
                              placeholder="Select Title"
                              error={errors.jobTitle?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="billingRatePerHour">Billing Rate Per Hourly</Label>
                        <Input
                          type="number"
                          step="0.01"
                          {...register("billingRatePerHour")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${errors.billingRatePerHour ? "border-red-500" : ""}`}
                          placeholder="Enter Billing rate"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.billingRatePerHour?.message} className="mt-1" />
                      </div>
                      <div>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Status"
                              options={["Active", "Inactive"]}
                              placeholder="Select Status"
                              error={errors.status?.message}
                              required={true}
                              searchable={false}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Authentication */}
                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Authentication
                    </Typography>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Controller
                          name="sendInviteEmail"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              id="sendInviteEmail"
                              checked={field.value}
                              label="Send invite email to set password"
                              disabled={true}
                            />
                          )}
                        />
                        <Button
                          type="button"
                          aria-label="Passwords can only be set by users for security reasons"
                          className="text-sm border-0 !shadow-none !bg-transparent !p-0"
                          data-tooltip="Passwords can only be set by users for security reasons"
                        >
                          <RiInformationLine
                            aria-hidden
                            className="text-base text-gray-500 hover:text-gray-700"
                          />
                        </Button>
                      </div>
                      <Controller
                        name="forcePasswordResetOnFirstLogin"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            id="forcePasswordResetOnFirstLogin"
                            checked={field.value}
                            label="Force password reset on first login"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Section 4: Contact */}
                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <ExpandableSection
                      title="Contact details (optional)"
                      expanded={isContactExpanded}
                      onToggle={() => setIsContactExpanded(!isContactExpanded)}
                    >
                      {isContactExpanded && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="mobileNumber">Mobile Number</Label>
                            <Controller
                              name="mobileNumber"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="tel"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(digitsOnly(e.target.value))}
                                  className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                                  id="mobileNumber"
                                  placeholder="Enter mobile number"
                                  noWrapper={true}
                                />
                              )}
                            />
                          </div>
                          <div>
                            <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                            <Controller
                              name="whatsappNumber"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="tel"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(digitsOnly(e.target.value))}
                                  className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                                  id="whatsappNumber"
                                  placeholder="Enter WhatsApp number"
                                  noWrapper={true}
                                />
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </ExpandableSection>
                  </div>

                  {/* Section 5: Preferences */}
                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Preferences
                    </Typography>
                    <Controller
                      name="receiveWeeklyActivitySummary"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          id="receiveWeeklyActivitySummary"
                          checked={field.value}
                          label="Receive weekly activity summary"
                        />
                      )}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-surface-primary border-t border-border-primary shadow-lg z-10 py-4 px-6">
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="danger"
            disabled={loading}
            onClick={() => navigate(prevPath)}
            className="btn-reset"
          >
            Cancel
          </Button>
          <FormSubmitButton
            type="button"
            loading={loading}
            label="Add user"
            idleIconClass="ri-add-circle-line"
            onClick={handleSubmit(onSubmit)}
            className="font-bold"
          />
        </div>
      </div>
    </div>
  );
}
